// src/components/SceneLayers/Nodes/Node/Node.tsx
/**
 * FIXED Node Component - Prevents style inheritance to icons
 */

import { useMemo, useState, useEffect } from 'react';
import { Box, Typography, Stack, Link } from '@mui/material';
import {
  PROJECTED_TILE_SIZE,
  DEFAULT_LABEL_HEIGHT
} from 'src/config';
import { getTilePosition } from 'src/utils';
import { useIcon } from 'src/hooks/useIcon';
import { ViewItem } from 'src/types';
import { useModelItem } from 'src/hooks/useModelItem';
import { Label } from 'src/components/Label/Label';
import { useIconReplacementState } from 'src/hooks/useIconReplacementState';
import { useUiStateStore } from '@/stores/uiStateStore';

interface Props {
  node: ViewItem;
  order: number;
}

const DESCRIPTION_TRUNCATE_LENGTH = 120;

export const Node = ({ node, order }: Props) => {
  const uiActions = useUiStateStore((state) => state.actions);
  const modelItem = useModelItem(node.id);
  const { iconComponent } = useIcon(modelItem?.icon || '', node.id);
  const shouldHide = useIconReplacementState(node.id);
  const [isDark, setIsDark] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Watch for dark mode changes by observing the 'dark' class on document.documentElement
  useEffect(() => {
    const checkTheme = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };

    // Check initial theme
    checkTheme();

    // Watch for class changes on document element
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const position = useMemo(() => {
    return getTilePosition({
      tile: node.tile,
      origin: 'BOTTOM'
    });
  }, [node.tile]);

  const description = useMemo(() => {
    if (!modelItem?.description || modelItem.description.trim() === '')
      return null;

    return modelItem.description;
  }, [modelItem?.description]);

  const shouldTruncate = useMemo(() => {
    return description && description.length > DESCRIPTION_TRUNCATE_LENGTH;
  }, [description]);

  const displayedDescription = useMemo(() => {
    if (!description) return null;
    if (!shouldTruncate || isDescriptionExpanded) return description;
    return description.slice(0, DESCRIPTION_TRUNCATE_LENGTH).trim() + '...';
  }, [description, shouldTruncate, isDescriptionExpanded]);

  // Early return if model item not found (after all hooks)
  if (!modelItem) {
    return null;
  }

  return (
    <Box
      data-scene-item-type="ITEM"
      data-scene-item-id={node.id}
      draggable={false}
      onDragStart={(event) => {
        // Canvas nodes are moved by the tile interaction manager. Allowing the
        // browser's native image drag creates a detached ghost and steals the
        // mouseup event, leaving the canvas drag bound to the pointer.
        event.preventDefault();
      }}
      onDoubleClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        uiActions.setItemControls({ type: 'ITEM', id: node.id });
        uiActions.setEditingModelItemId(node.id);
      }}
      sx={{
        position: 'absolute',
        zIndex: order
      }}
    >
      <Box
        sx={{ position: 'absolute' }}
        style={{
          left: position.x,
          top: position.y
        }}
      >
        {(modelItem.name || description) && (
          <Box
            sx={{ position: 'absolute' }}
            style={{ bottom: PROJECTED_TILE_SIZE.height / 2 + 12 }}
          >
            <Label
              maxWidth={200}
              expandDirection="BOTTOM"
              labelHeight={node.labelHeight ?? DEFAULT_LABEL_HEIGHT}
            >
              <Stack spacing={1.5}>
                {modelItem.name && (
                  <Typography
                    fontWeight={600}
                    fontSize="0.875rem"
                    lineHeight={1.3}
                    sx={{
                      color: isDark ? '#ffffff' : '#111827'
                    }}
                  >
                    {modelItem.name}
                  </Typography>
                )}
                {description && (
                  <Box>
                    <Typography
                      component="span"
                      fontSize="0.8rem"
                      lineHeight={1.45}
                      sx={{
                        color: isDark
                          ? 'rgba(255, 255, 255, 0.85)'
                          : 'rgba(55, 65, 81, 1)',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        ...(!isDescriptionExpanded && {
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 2,
                          overflow: 'hidden'
                        })
                      }}
                    >
                      {displayedDescription}
                    </Typography>
                    {shouldTruncate && (
                      <Link
                        component="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsDescriptionExpanded(!isDescriptionExpanded);
                        }}
                        sx={{
                          ml: 0.5,
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          color: isDark
                            ? 'rgba(96, 165, 250, 0.9)'
                            : 'rgba(59, 130, 246, 1)',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {isDescriptionExpanded ? 'Show less' : 'Read more'}
                      </Link>
                    )}
                  </Box>
                )}
              </Stack>
            </Label>
          </Box>
        )}
        {iconComponent && (
          <Box
            sx={{
              position: 'absolute',
              pointerEvents: 'auto',
              opacity: shouldHide ? 0 : 1, // ✨ NEW: Hide when being replaced
              transition: 'opacity 0.15s ease-in-out', // ✨ NEW: Smooth transition
              // CRITICAL FIX: Override any inherited styles
              '& img': {
                WebkitUserDrag: 'none',
                userSelect: 'none',
                // Reset any inherited styles from sidebar/parent
                width: 'auto !important',
                height: 'auto !important',
                maxWidth: 'none !important',
                maxHeight: 'none !important',
                minWidth: 'unset !important',
                minHeight: 'unset !important',
                objectFit: 'contain !important',
                // Remove any inherited classes
                className: 'none'
              },
              // Also handle Box components inside
              '& > div': {
                width: 'auto !important',
                height: 'auto !important',
                maxWidth: 'none !important',
                maxHeight: 'none !important'
              }
            }}
          >
            {iconComponent}
          </Box>
        )}
      </Box>
    </Box>
  );
};
